import { Trans } from 'react-i18next';

import type { IBannerConfigMapParams } from '@app/dashboard/types/innerLayout/home/onboarding';
import {
  ONBOARDING_CONVERTED_STATUS,
  ONBOARDING_STATES,
  ONBOARDING_WITHOUT_STATES,
  SPECIAL_HANDLERS,
  SUPPORT_EMAIL,
} from '@app/dashboard/constants/Home/onboarding';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import {
  addToLocalStorage,
  getFromLocalStorage,
} from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { ONBOARDING_BANNER_EVENTS } from '@app/dashboard/constants/tracking_events/home';

export const buildBannerConfigMap = ({
  t,
  country,
  isLoading,
  handlers,
}: IBannerConfigMapParams) => {
  const isEGY = country === COUNTRIES.EGY;
  const LIVE_KEY_SUFFIX = isEGY ? 'Egypt' : 'NonEgypt';

  const makeBtnProps = (text: string, state: string, event: string) => ({
    text,
    onClickHandler: () => {
      const handler = handlers[state as keyof typeof handlers];
      if (handler) handler({ eventName: event });
    },
    loading: isLoading,
    disabled: isLoading,
  });

  return {
    [ONBOARDING_STATES.PENDING_ONBOARDING]: {
      status: ONBOARDING_CONVERTED_STATUS.BRAND,
      title: t('innerLayout.homePage.onboardingBanner.pendingOnboarding.title'),
      btnProps: makeBtnProps(
        t('innerLayout.homePage.onboardingBanner.pendingOnboarding.buttonText'),
        ONBOARDING_STATES.PENDING_ONBOARDING,
        ONBOARDING_BANNER_EVENTS.COMPLETE_ONBOARDING_BTN_CLICK
      ),
    },

    [ONBOARDING_STATES.IN_ONBOARDING_PROCESS]: {
      status: ONBOARDING_CONVERTED_STATUS.WARNING,
      title: t(
        'innerLayout.homePage.onboardingBanner.inOnboardingProcess.title'
      ),
      subtitle: t(
        'innerLayout.homePage.onboardingBanner.inOnboardingProcess.subtitle'
      ),
      btnProps: makeBtnProps(
        t(
          'innerLayout.homePage.onboardingBanner.inOnboardingProcess.buttonText'
        ),
        ONBOARDING_STATES.IN_ONBOARDING_PROCESS,
        ONBOARDING_BANNER_EVENTS.COMPLETE_ONBOARDING_BTN_CLICK
      ),
    },

    [ONBOARDING_STATES.PENDING_VERIFICATION]: {
      status: ONBOARDING_CONVERTED_STATUS.BRAND,
      title: t(
        'innerLayout.homePage.onboardingBanner.pendingVerification.title'
      ),
      subtitle: t(
        'innerLayout.homePage.onboardingBanner.pendingVerification.subtitle'
      ),
    },

    [ONBOARDING_STATES.UPDATES_REQUIRED]: {
      status: ONBOARDING_CONVERTED_STATUS.ERROR,
      title: t('innerLayout.homePage.onboardingBanner.updatesRequired.title'),
      subtitle: t(
        'innerLayout.homePage.onboardingBanner.updatesRequired.subtitle'
      ),
      btnProps: makeBtnProps(
        t('innerLayout.homePage.onboardingBanner.updatesRequired.buttonText'),
        ONBOARDING_STATES.UPDATES_REQUIRED,
        ONBOARDING_BANNER_EVENTS.VIEW_ISSUES_BTN_CLICK
      ),
    },

    [ONBOARDING_STATES.REJECTED]: {
      status: ONBOARDING_CONVERTED_STATUS.ERROR,
      title: t('innerLayout.homePage.onboardingBanner.rejected.title'),
      subtitle: (
        <Trans
          values={{ email: SUPPORT_EMAIL }}
          i18nKey="innerLayout.homePage.onboardingBanner.rejected.subtitle"
          components={{
            email: (
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                data-onboarding-banner-email="true"
                onClick={() =>
                  handlers[SPECIAL_HANDLERS.MAIL_SUPPORT](
                    ONBOARDING_BANNER_EVENTS.SUPPORT_BTN_CLICK
                  )
                }
              >
                {SUPPORT_EMAIL}
              </a>
            ),
          }}
        />
      ),
    },

    [ONBOARDING_STATES.LIVE]: {
      status: ONBOARDING_CONVERTED_STATUS.SUCCESS,
      title: t(
        `innerLayout.homePage.onboardingBanner.live${LIVE_KEY_SUFFIX}.title`
      ),
      subtitle: t(
        `innerLayout.homePage.onboardingBanner.live${LIVE_KEY_SUFFIX}.subtitle`
      ),
      ...(!isEGY && {
        btnProps: makeBtnProps(
          t('innerLayout.homePage.onboardingBanner.liveNonEgypt.buttonText'),
          ONBOARDING_STATES.LIVE,
          ONBOARDING_BANNER_EVENTS.VIEW_PAYMENT_STATUS_BTN_CLICK
        ),
      }),
    },

    [ONBOARDING_WITHOUT_STATES.NO_ONBOARDING]: {
      status: ONBOARDING_CONVERTED_STATUS.ERROR,
      title: t(
        'innerLayout.homePage.onboardingBanner.noOnboardingProfile.title'
      ),
    },

    [ONBOARDING_WITHOUT_STATES.INVALID_PROFILE]: {
      status: ONBOARDING_CONVERTED_STATUS.ERROR,
      title: t('innerLayout.homePage.onboardingBanner.invalidProfile.title'),
    },

    [ONBOARDING_WITHOUT_STATES.ONBOARDING_WITH_ANOTHER_PARTNER]: {
      status: ONBOARDING_CONVERTED_STATUS.BRAND,
      title: t('innerLayout.homePage.onboardingBanner.alreadyOnboarded.title'),
      subtitle: t(
        'innerLayout.homePage.onboardingBanner.alreadyOnboarded.subtitle'
      ),
    },
  };
};

export const setLiveBannerSeen = () => {
  addToLocalStorage(LOCAL_STORAGE_KEYS.LIVE_ONBOARDING_BANNER_SEEN, true);
};

export const hasSeenLiveBanner = () => {
  return getFromLocalStorage(LOCAL_STORAGE_KEYS.LIVE_ONBOARDING_BANNER_SEEN);
};
